/**
 * Stage 1 — Grasslands tutorial (M94).
 * Exactly 15 Levels (1-1 → 1-15). Shorter than Stage 2 by design.
 */

import type {
  ChapterDefinition,
  StageDefinition,
  StageWaveSpec
} from '../combat/campaign/types'
import type { DifficultyId } from '../combat/types/primitives'
import type { StageFirstClearRewards } from '../combat/campaign/firstClear'
import {
  chapter1LevelPlan,
  chapter1TowerTier,
  chapter1WaveId,
  g,
  w
} from './authoring'

const ALL_DIFFICULTIES: DifficultyId[] = ['easy', 'normal', 'hard', 'insane']

/**
 * First-clear Ingots — flatter than legacy 10-stage curve so 15 Levels stay
 * inside the Research mastery runway (prices unchanged).
 * base = 5 + floor(ordinal * 0.8)
 */
function rewards(stageOrdinal: number): StageFirstClearRewards {
  const base = 5 + Math.floor(stageOrdinal * 0.8)
  return {
    easy: base,
    normal: base * 2,
    hard: base * 4,
    insane: base * 8
  }
}

function stage(
  ordinal: number,
  waves: StageWaveSpec[]
): StageDefinition {
  const plan = chapter1LevelPlan(ordinal)
  return {
    id: `chapter1-stage${ordinal}`,
    chapterId: 'chapter-1',
    displayLabel: `1-${ordinal}`,
    mapId: plan.mapId,
    allowedTowerIds: [...(plan.allowedTowerIds ?? chapter1TowerTier(ordinal))],
    availableDifficulties: ALL_DIFFICULTIES,
    firstClearRewards: rewards(ordinal),
    bossLevel: plan.bossLevel,
    waves
  }
}

function wid(ordinal: number, wave: number): string {
  return chapter1WaveId(ordinal, wave)
}

export const CHAPTER_1: ChapterDefinition = {
  id: 'chapter-1',
  campaignId: 'campaign-main',
  displayName: 'Stage 1 — Grasslands',
  chapterNumber: 1,
  biomeId: 'grasslands',
  enemyFamilyIds: ['troll'],
  stageIds: Array.from({ length: 15 }, (_, i) => `chapter1-stage${i + 1}`)
}

/**
 * Grasslands tutorial — progressive tower unlocks FIRE → FROST → CANNON →
 * POISON → AA → MAGE. AIR only after AA (1-8+). Boss Levels at 1-5 / 1-10 / 1-15.
 */
export const CHAPTER_1_STAGES: StageDefinition[] = [
  // 1-1 FIRE / BASICS — Valley
  stage(1, [
    w(wid(1, 1), 25, [g('normal', 1, 3, 1.3, 0.5)]),
    w(wid(1, 2), 28, [g('normal', 1, 4, 1.15, 0.4)]),
    w(wid(1, 3), 32, [g('normal', 1, 5, 1.05, 0.35)]),
    w(wid(1, 4), 36, [g('normal', 1, 6, 0.95, 0.3)]),
    w(wid(1, 5), 42, [g('normal', 1, 7, 0.9, 0.3)])
  ]),

  // 1-2 FROST / GROUPS — Valley
  stage(2, [
    w(wid(2, 1), 28, [g('normal', 1, 4, 0.85, 0.4)]),
    w(wid(2, 2), 32, [
      g('normal', 1, 4, 0.75, 0.3),
      g('normal', 1, 3, 0.75, 1.1)
    ]),
    w(wid(2, 3), 36, [g('normal', 1, 8, 0.7, 0.3)]),
    w(wid(2, 4), 40, [
      g('normal', 1, 5, 0.7, 0.25),
      g('normal', 2, 4, 0.7, 1.0)
    ]),
    w(wid(2, 5), 48, [g('normal', 2, 9, 0.65, 0.3)])
  ]),

  // 1-3 FAST — Switchback
  stage(3, [
    w(wid(3, 1), 30, [g('normal', 1, 5, 0.95, 0.4)]),
    w(wid(3, 2), 34, [g('fast', 1, 5, 0.65, 0.3)]),
    w(wid(3, 3), 38, [
      g('normal', 2, 4, 0.85, 0.3),
      g('fast', 1, 4, 0.6, 0.8)
    ]),
    w(wid(3, 4), 44, [g('fast', 2, 6, 0.55, 0.3)]),
    w(wid(3, 5), 52, [
      g('normal', 2, 5, 0.75, 0.25),
      g('fast', 2, 5, 0.55, 0.6)
    ])
  ]),

  // 1-4 CANNON / TANK — Valley
  stage(4, [
    w(wid(4, 1), 35, [g('normal', 2, 8, 0.75, 0.4)]),
    w(wid(4, 2), 42, [
      g('tank', 2, 3, 1.15, 0.4),
      g('normal', 2, 4, 0.7, 0.8)
    ]),
    w(wid(4, 3), 50, [
      g('tank', 2, 4, 1.0, 0.3),
      g('fast', 2, 4, 0.55, 1.0)
    ]),
    w(wid(4, 4), 58, [
      g('tank', 3, 5, 0.95, 0.3),
      g('normal', 2, 5, 0.65, 0.55)
    ]),
    w(wid(4, 5), 70, [
      g('tank', 3, 6, 0.85, 0.25),
      g('normal', 3, 6, 0.6, 0.5)
    ])
  ]),

  // 1-5 POISON / REGEN + BOSS — Crossroads
  stage(5, [
    w(wid(5, 1), 35, [g('normal', 2, 8, 0.7, 0.4)]),
    w(wid(5, 2), 40, [g('fast', 2, 7, 0.5, 0.3)]),
    w(wid(5, 3), 48, [g('regen', 2, 5, 1.0, 0.5)]),
    w(wid(5, 4), 55, [
      g('tank', 2, 2, 1.8, 0.4),
      g('regen', 3, 4, 0.95, 1.0)
    ]),
    w(wid(5, 5), 65, [
      g('normal', 3, 7, 0.6, 0.3),
      g('regen', 3, 4, 0.9, 0.7)
    ]),
    w(wid(5, 6), 180, [g('boss', 3, 1, 0, 1.2)])
  ]),

  // 1-6 SHIELD — Switchback (softened for STANDARD reference clear)
  stage(6, [
    w(wid(6, 1), 40, [g('normal', 2, 6, 0.75, 0.4)]),
    w(wid(6, 2), 45, [g('shield', 2, 3, 1.4, 0.5)]),
    w(wid(6, 3), 52, [
      g('tank', 2, 1, 2.0, 0.4),
      g('shield', 2, 2, 1.35, 1.0)
    ]),
    w(wid(6, 4), 58, [
      g('fast', 2, 5, 0.55, 0.3),
      g('regen', 2, 2, 1.2, 0.9)
    ]),
    w(wid(6, 5), 68, [
      g('shield', 3, 2, 1.25, 0.3),
      g('tank', 2, 1, 1.8, 1.0)
    ]),
    w(wid(6, 6), 78, [
      g('shield', 3, 3, 1.2, 0.3),
      g('tank', 3, 2, 1.6, 1.1)
    ])
  ]),

  // 1-7 ARMOR MIX — Rift (pre-AA ground finale)
  stage(7, [
    w(wid(7, 1), 42, [g('normal', 2, 6, 0.75, 0.4)]),
    w(wid(7, 2), 48, [g('fast', 2, 6, 0.6, 0.3)]),
    w(wid(7, 3), 55, [
      g('tank', 2, 1, 1.9, 0.4),
      g('shield', 2, 2, 1.35, 0.9)
    ]),
    w(wid(7, 4), 60, [
      g('regen', 2, 2, 1.15, 0.35),
      g('normal', 2, 5, 0.7, 0.7)
    ]),
    w(wid(7, 5), 68, [
      g('shield', 3, 2, 1.25, 0.3),
      g('fast', 2, 5, 0.55, 0.8)
    ]),
    w(wid(7, 6), 75, [
      g('tank', 3, 1, 1.7, 0.35),
      g('regen', 3, 2, 1.1, 0.9)
    ]),
    w(wid(7, 7), 88, [
      g('shield', 3, 2, 1.2, 0.3),
      g('tank', 3, 1, 1.6, 0.85),
      g('fast', 3, 4, 0.55, 1.2)
    ])
  ]),

  // 1-8 AA / AIR INTRO — Skew
  stage(8, [
    w(wid(8, 1), 45, [
      g('normal', 3, 4, 0.8, 0.4, 'NORMAL', 'ground-a'),
      g('normal', 3, 3, 0.8, 0.5, 'NORMAL', 'ground-b')
    ]),
    w(wid(8, 2), 50, [g('flyer', 3, 4, 0.85, 0.4, 'NORMAL', 'ground-a')]),
    w(wid(8, 3), 55, [
      g('normal', 3, 4, 0.8, 0.3, 'NORMAL', 'ground-a'),
      g('flyer', 3, 3, 0.8, 0.7, 'NORMAL', 'ground-a')
    ]),
    w(wid(8, 4), 62, [
      g('tank', 3, 1, 2.0, 0.4, 'NORMAL', 'ground-b'),
      g('flyer', 3, 4, 0.75, 0.7, 'NORMAL', 'ground-a')
    ]),
    w(wid(8, 5), 70, [
      g('fast', 3, 4, 0.55, 0.3, 'NORMAL', 'ground-a'),
      g('flyer', 3, 4, 0.65, 0.45, 'NORMAL', 'ground-a')
    ]),
    w(wid(8, 6), 78, [
      g('shield', 3, 1, 1.4, 0.3, 'NORMAL', 'ground-b'),
      g('flyer', 4, 4, 0.62, 0.4, 'NORMAL', 'ground-a')
    ]),
    w(wid(8, 7), 85, [
      g('normal', 4, 5, 0.65, 0.3, 'NORMAL', 'ground-a'),
      g('flyer', 4, 4, 0.6, 0.5, 'NORMAL', 'ground-b')
    ]),
    w(wid(8, 8), 95, [
      g('tank', 4, 1, 1.6, 0.35, 'NORMAL', 'ground-b'),
      g('flyer', 4, 5, 0.55, 0.4, 'NORMAL', 'ground-a')
    ])
  ]),

  // 1-9 DUAL + AIR — Twinspire
  stage(9, [
    w(wid(9, 1), 50, [
      g('normal', 3, 4, 0.75, 0.3, 'NORMAL', 'ground-a'),
      g('normal', 3, 4, 0.75, 0.35, 'NORMAL', 'ground-b')
    ]),
    w(wid(9, 2), 55, [
      g('fast', 3, 4, 0.55, 0.3, 'NORMAL', 'ground-a'),
      g('flyer', 3, 3, 0.75, 0.5, 'NORMAL', 'ground-a')
    ]),
    w(wid(9, 3), 60, [
      g('tank', 4, 1, 1.8, 0.4, 'NORMAL', 'ground-b'),
      g('fast', 3, 4, 0.55, 0.3, 'NORMAL', 'ground-a')
    ]),
    w(wid(9, 4), 65, [
      g('shield', 4, 2, 1.3, 0.3, 'NORMAL', 'ground-a'),
      g('regen', 4, 2, 1.2, 0.5, 'NORMAL', 'ground-b')
    ]),
    w(wid(9, 5), 72, [
      g('flyer', 4, 4, 0.65, 0.3, 'NORMAL', 'ground-a'),
      g('normal', 4, 4, 0.65, 0.4, 'NORMAL', 'ground-b')
    ]),
    w(wid(9, 6), 80, [
      g('fast', 4, 5, 0.5, 0.25, 'NORMAL', 'ground-a'),
      g('tank', 4, 1, 1.7, 0.8, 'NORMAL', 'ground-b')
    ]),
    w(wid(9, 7), 88, [
      g('shield', 4, 2, 1.2, 0.3, 'NORMAL', 'ground-b'),
      g('flyer', 4, 3, 0.6, 0.4, 'NORMAL', 'ground-a')
    ]),
    w(wid(9, 8), 100, [
      g('tank', 5, 1, 1.5, 0.4, 'NORMAL', 'ground-a'),
      g('tank', 5, 1, 1.5, 0.5, 'NORMAL', 'ground-b'),
      g('flyer', 5, 3, 0.55, 0.4, 'NORMAL', 'ground-a')
    ])
  ]),

  // 1-10 MID BOSS — Confluence (AA kit, no Mage yet)
  stage(10, [
    w(wid(10, 1), 55, [
      g('normal', 3, 4, 0.85, 0.3, 'NORMAL', 'ground-a'),
      g('normal', 3, 3, 0.85, 0.4, 'NORMAL', 'ground-b')
    ]),
    w(wid(10, 2), 60, [
      g('fast', 3, 4, 0.6, 0.3, 'NORMAL', 'ground-a'),
      g('fast', 3, 3, 0.6, 0.85, 'NORMAL', 'ground-b')
    ]),
    w(wid(10, 3), 68, [
      g('flyer', 3, 3, 0.75, 0.4, 'NORMAL', 'ground-a'),
      g('tank', 4, 1, 1.9, 0.6, 'NORMAL', 'ground-b')
    ]),
    w(wid(10, 4), 75, [
      g('regen', 4, 2, 1.35, 0.3, 'NORMAL', 'ground-a'),
      g('shield', 4, 1, 1.35, 0.5, 'NORMAL', 'ground-b')
    ]),
    w(wid(10, 5), 82, [
      g('fast', 4, 4, 0.55, 0.3, 'NORMAL', 'ground-a'),
      g('flyer', 4, 3, 0.7, 0.5, 'NORMAL', 'ground-a')
    ]),
    w(wid(10, 6), 90, [
      g('tank', 4, 1, 1.65, 0.4, 'NORMAL', 'ground-a'),
      g('tank', 4, 1, 1.65, 0.5, 'NORMAL', 'ground-b'),
      g('normal', 4, 4, 0.65, 0.6, 'NORMAL', 'ground-a')
    ]),
    w(wid(10, 7), 100, [
      g('shield', 3, 1, 1.4, 0.3, 'NORMAL', 'ground-b'),
      g('flyer', 3, 2, 0.75, 0.4, 'NORMAL', 'ground-a')
    ]),
    w(wid(10, 8), 140, [g('boss', 4, 1, 0, 1.4, 'NORMAL', 'ground-a')]),
    w(wid(10, 9), 180, [
      g('flyer', 4, 3, 0.8, 0.5, 'NORMAL', 'ground-a'),
      g('tank', 4, 1, 1.8, 1.2, 'NORMAL', 'ground-b')
    ])
  ]),

  // 1-11 MAGE / ARMOR BREAK — Crossroads
  stage(11, [
    w(wid(11, 1), 55, [g('normal', 3, 6, 0.7, 0.4)]),
    w(wid(11, 2), 60, [g('tank', 4, 3, 1.5, 0.4)]),
    w(wid(11, 3), 65, [g('shield', 4, 3, 1.2, 0.4)]),
    w(wid(11, 4), 72, [
      g('tank', 4, 2, 1.4, 0.3),
      g('shield', 4, 2, 1.1, 0.8),
      g('flyer', 4, 3, 0.7, 0.5, 'NORMAL', 'ground-a')
    ]),
    w(wid(11, 5), 80, [
      g('tank', 5, 2, 1.35, 0.3),
      g('shield', 5, 2, 1.05, 0.7),
      g('regen', 4, 2, 1.2, 1.1)
    ]),
    w(wid(11, 6), 88, [
      g('shield', 5, 3, 1.0, 0.25),
      g('flyer', 5, 3, 0.6, 0.45, 'NORMAL', 'ground-a')
    ]),
    w(wid(11, 7), 95, [
      g('tank', 5, 3, 1.3, 0.3),
      g('shield', 5, 2, 1.05, 0.7)
    ]),
    w(wid(11, 8), 110, [
      g('shield', 5, 4, 1.0, 0.25),
      g('tank', 5, 3, 1.25, 0.7),
      g('flyer', 5, 4, 0.55, 0.45, 'NORMAL', 'ground-a')
    ])
  ]),

  // 1-12 MIXED PRESSURE — Switchback
  stage(12, [
    w(wid(12, 1), 58, [g('normal', 4, 7, 0.7, 0.35)]),
    w(wid(12, 2), 64, [
      g('fast', 4, 6, 0.5, 0.3),
      g('flyer', 4, 3, 0.7, 0.6, 'NORMAL', 'ground-a')
    ]),
    w(wid(12, 3), 70, [
      g('tank', 4, 2, 1.55, 0.35),
      g('shield', 4, 2, 1.2, 0.9)
    ]),
    w(wid(12, 4), 76, [
      g('regen', 4, 3, 1.1, 0.35),
      g('normal', 4, 5, 0.65, 0.7)
    ]),
    w(wid(12, 5), 82, [
      g('flyer', 4, 4, 0.65, 0.3, 'NORMAL', 'ground-a'),
      g('fast', 4, 5, 0.5, 0.55)
    ]),
    w(wid(12, 6), 90, [
      g('shield', 5, 2, 1.15, 0.3),
      g('tank', 5, 2, 1.4, 0.85),
      g('flyer', 5, 2, 0.7, 1.1, 'NORMAL', 'ground-a')
    ]),
    w(wid(12, 7), 98, [
      g('fast', 5, 6, 0.48, 0.25),
      g('regen', 5, 2, 1.1, 0.8)
    ]),
    w(wid(12, 8), 105, [
      g('tank', 5, 2, 1.35, 0.3),
      g('shield', 5, 2, 1.1, 0.7),
      g('flyer', 5, 3, 0.6, 0.5, 'NORMAL', 'ground-a')
    ]),
    w(wid(12, 9), 120, [
      g('shield', 5, 3, 1.05, 0.25),
      g('tank', 5, 2, 1.3, 0.7),
      g('flyer', 5, 4, 0.55, 0.4, 'NORMAL', 'ground-a')
    ])
  ]),

  // 1-13 ELITE MIX — Twinspire
  stage(13, [
    w(wid(13, 1), 60, [
      g('normal', 4, 4, 0.7, 0.3, 'NORMAL', 'ground-a'),
      g('normal', 4, 4, 0.7, 0.35, 'NORMAL', 'ground-b')
    ]),
    w(wid(13, 2), 66, [
      g('fast', 4, 4, 0.52, 0.3, 'NORMAL', 'ground-a'),
      g('flyer', 4, 3, 0.7, 0.5, 'NORMAL', 'ground-a')
    ]),
    w(wid(13, 3), 72, [
      g('tank', 5, 1, 1.7, 0.4, 'NORMAL', 'ground-b'),
      g('fast', 4, 5, 0.5, 0.3, 'NORMAL', 'ground-a')
    ]),
    w(wid(13, 4), 78, [
      g('shield', 5, 2, 1.25, 0.3, 'NORMAL', 'ground-a'),
      g('regen', 5, 2, 1.15, 0.5, 'NORMAL', 'ground-b')
    ]),
    w(wid(13, 5), 85, [
      g('flyer', 5, 4, 0.62, 0.3, 'NORMAL', 'ground-a'),
      g('normal', 5, 4, 0.62, 0.4, 'NORMAL', 'ground-b')
    ]),
    w(wid(13, 6), 92, [
      g('fast', 5, 5, 0.48, 0.25, 'NORMAL', 'ground-a'),
      g('tank', 5, 1, 1.6, 0.8, 'NORMAL', 'ground-b')
    ]),
    w(wid(13, 7), 100, [
      g('shield', 5, 2, 1.15, 0.3, 'NORMAL', 'ground-b'),
      g('flyer', 5, 3, 0.58, 0.4, 'NORMAL', 'ground-a'),
      g('flyer', 5, 1, 0.9, 1.0, 'ELITE', 'ground-a')
    ]),
    w(wid(13, 8), 108, [
      g('tank', 5, 1, 1.45, 0.35, 'NORMAL', 'ground-a'),
      g('tank', 5, 1, 1.45, 0.45, 'NORMAL', 'ground-b'),
      g('flyer', 5, 3, 0.55, 0.4, 'NORMAL', 'ground-a')
    ]),
    w(wid(13, 9), 115, [
      g('shield', 5, 2, 1.1, 0.3, 'NORMAL', 'ground-a'),
      g('regen', 5, 2, 1.1, 0.5, 'NORMAL', 'ground-b'),
      g('flyer', 5, 3, 0.55, 0.4, 'NORMAL', 'ground-b')
    ]),
    w(wid(13, 10), 130, [
      g('tank', 5, 2, 1.35, 0.3, 'NORMAL', 'ground-a'),
      g('shield', 5, 2, 1.1, 0.6, 'NORMAL', 'ground-b'),
      g('flyer', 5, 4, 0.5, 0.35, 'NORMAL', 'ground-a')
    ])
  ]),

  // 1-14 PRESSURE — Skew (softened for STANDARD clear)
  stage(14, [
    w(wid(14, 1), 62, [
      g('normal', 3, 4, 0.8, 0.3, 'NORMAL', 'ground-a'),
      g('normal', 3, 3, 0.8, 0.4, 'NORMAL', 'ground-b')
    ]),
    w(wid(14, 2), 68, [
      g('fast', 3, 4, 0.6, 0.3, 'NORMAL', 'ground-a'),
      g('flyer', 3, 2, 0.8, 0.55, 'NORMAL', 'ground-a')
    ]),
    w(wid(14, 3), 74, [
      g('tank', 4, 1, 1.8, 0.35, 'NORMAL', 'ground-b'),
      g('shield', 4, 1, 1.35, 0.7, 'NORMAL', 'ground-a')
    ]),
    w(wid(14, 4), 80, [
      g('regen', 4, 2, 1.25, 0.3, 'NORMAL', 'ground-a'),
      g('flyer', 4, 2, 0.75, 0.5, 'NORMAL', 'ground-b')
    ]),
    w(wid(14, 5), 88, [
      g('fast', 4, 4, 0.55, 0.25, 'NORMAL', 'ground-a'),
      g('flyer', 4, 2, 0.8, 0.9, 'NORMAL', 'ground-a')
    ]),
    w(wid(14, 6), 95, [
      g('shield', 4, 1, 1.3, 0.3, 'NORMAL', 'ground-b'),
      g('normal', 4, 4, 0.7, 0.4, 'NORMAL', 'ground-a')
    ]),
    w(wid(14, 7), 102, [
      g('flyer', 4, 3, 0.7, 0.3, 'NORMAL', 'ground-a'),
      g('regen', 4, 1, 1.25, 0.7, 'NORMAL', 'ground-b')
    ]),
    w(wid(14, 8), 110, [
      g('tank', 4, 1, 1.55, 0.3, 'NORMAL', 'ground-a'),
      g('flyer', 4, 3, 0.65, 0.4, 'NORMAL', 'ground-a')
    ]),
    w(wid(14, 9), 118, [
      g('fast', 4, 5, 0.55, 0.25, 'NORMAL', 'ground-a'),
      g('flyer', 4, 2, 0.7, 0.5, 'NORMAL', 'ground-a')
    ]),
    w(wid(14, 10), 135, [
      g('tank', 4, 1, 1.5, 0.3, 'NORMAL', 'ground-a'),
      g('shield', 4, 1, 1.2, 0.7, 'NORMAL', 'ground-b'),
      g('flyer', 4, 3, 0.6, 0.35, 'NORMAL', 'ground-a')
    ])
  ]),

  // 1-15 FINALE — Confluence dual + bosses
  stage(15, [
    w(wid(15, 1), 70, [
      g('normal', 4, 4, 0.85, 0.3, 'NORMAL', 'ground-a'),
      g('normal', 4, 3, 0.85, 0.4, 'NORMAL', 'ground-b')
    ]),
    w(wid(15, 2), 75, [
      g('fast', 4, 4, 0.6, 0.3, 'NORMAL', 'ground-a'),
      g('fast', 4, 3, 0.6, 0.85, 'NORMAL', 'ground-b')
    ]),
    w(wid(15, 3), 82, [
      g('flyer', 4, 3, 0.75, 0.4, 'NORMAL', 'ground-a'),
      g('tank', 5, 1, 1.85, 0.6, 'NORMAL', 'ground-b')
    ]),
    w(wid(15, 4), 90, [
      g('regen', 5, 2, 1.3, 0.3, 'NORMAL', 'ground-a'),
      g('shield', 5, 2, 1.3, 0.5, 'NORMAL', 'ground-b')
    ]),
    w(wid(15, 5), 98, [
      g('fast', 5, 4, 0.52, 0.3, 'NORMAL', 'ground-a'),
      g('flyer', 5, 3, 0.65, 0.5, 'NORMAL', 'ground-a')
    ]),
    w(wid(15, 6), 105, [
      g('tank', 5, 1, 1.6, 0.4, 'NORMAL', 'ground-a'),
      g('tank', 5, 1, 1.6, 0.5, 'NORMAL', 'ground-b'),
      g('normal', 5, 4, 0.62, 0.6, 'NORMAL', 'ground-a')
    ]),
    w(wid(15, 7), 112, [
      g('shield', 5, 2, 1.2, 0.3, 'NORMAL', 'ground-b'),
      g('flyer', 5, 3, 0.6, 0.4, 'NORMAL', 'ground-a')
    ]),
    w(wid(15, 8), 120, [
      g('fast', 5, 5, 0.48, 0.25, 'NORMAL', 'ground-a'),
      g('regen', 5, 2, 1.15, 0.7, 'NORMAL', 'ground-b'),
      g('flyer', 5, 2, 0.7, 0.9, 'NORMAL', 'ground-b')
    ]),
    w(wid(15, 9), 128, [
      g('tank', 5, 1, 1.5, 0.35, 'NORMAL', 'ground-a'),
      g('shield', 5, 2, 1.15, 0.55, 'NORMAL', 'ground-b'),
      g('flyer', 5, 3, 0.55, 0.4, 'NORMAL', 'ground-a')
    ]),
    w(wid(15, 10), 140, [
      g('tank', 5, 1, 1.45, 0.35, 'NORMAL', 'ground-a'),
      g('flyer', 5, 2, 0.7, 0.5, 'ELITE', 'ground-a')
    ]),
    w(wid(15, 11), 220, [g('boss', 5, 1, 0, 1.5, 'NORMAL', 'ground-a')]),
    w(wid(15, 12), 300, [
      g('air_boss', 5, 1, 0, 2.0, 'NORMAL', 'ground-a'),
      g('flyer', 5, 3, 0.9, 3.0, 'NORMAL', 'ground-a')
    ])
  ])
]

/** @deprecated Use registry STAGE_BY_ID — kept for chapter1-only tests. */
export const STAGE_BY_ID_CHAPTER_1: Record<string, StageDefinition> = Object.fromEntries(
  CHAPTER_1_STAGES.map((s) => [s.id, s])
)
